import {areSetsEqual, isSubset} from '@benchling/js-utils';

import {SchemaMapperConfig} from '../validation/ProcessGraphBuilderConstants';

function recursivelyGetMappers({
  inboundTaskSchemaIds,
  schemaMapperConfigs,
  mapperConfigsList,
  mappedTaskSchemaIds,
}: {
  inboundTaskSchemaIds: Set<string>;
  schemaMapperConfigs: Array<SchemaMapperConfig>;
  mapperConfigsList: Array<SchemaMapperConfig>;
  mappedTaskSchemaIds: Set<string>;
}): Array<SchemaMapperConfig> | null {
  if (areSetsEqual(inboundTaskSchemaIds, mappedTaskSchemaIds)) {
    return mapperConfigsList;
  }
  for (const mapperConfig of schemaMapperConfigs) {
    const fromTaskSchemaIds = new Set(mapperConfig.fromTaskSchemaIds);
    if (isSubset(fromTaskSchemaIds, inboundTaskSchemaIds)) {
      const mapperConfigsResult = recursivelyGetMappers({
        inboundTaskSchemaIds,
        schemaMapperConfigs: schemaMapperConfigs.filter(({clientId}) => clientId !== mapperConfig.clientId),
        mapperConfigsList: [...mapperConfigsList, mapperConfig],
        mappedTaskSchemaIds: new Set([...mappedTaskSchemaIds, ...fromTaskSchemaIds]),
      });
      if (mapperConfigsResult != null) {
        return mapperConfigsResult;
      }
    }
  }
  return null;
}

/**
 * Finds the list of schema mapper configs all of which map to the provided node based on the task schemas of
 * the parent nodes.
 *
 * Consider the following graph:
 *
 * ```
 *                (C)
 *            /        \
 *   (A) -> (B) -----> (E)
 *            \        /
 *               (D)
 * ```
 *
 * Where the design space has mapper configs which map:
 * - From task schema B AND C to E
 * - From task schema D to E
 * - From task schema A to E
 *
 * The ReactFlow flowchart representations only knows about nodes and edges - it doesn't reference mappers.
 * So we need to convert the set of parent nodes into the list of mappers that the user is implicitly attaching
 * to the node.
 *
 * We do this by finding the combination of schemaMapperConfigs in the design space whose `fromTaskSchemas` in
 * aggregate reference all of the task schemas of the node's parents.
 *
 * Ex) For the graph above we return the mappers B&C -> E and D -> E.
 *
 * @param node The node
 * @param inboundTaskSchemaIds The schemas of the nodes with edges to `node`
 * @param schemaMapperConfigs The list of schema mapper configs
 * @returns The list of schema mapper configs which covers the entire set of inboundTaskSchemaIds or null if
 * there is no combination of mapper configs which covers the set of inbound task schemas.
 */
export default function getSchemaMapperConfigsMappingToNode(
  node: {taskSchemaId: string},
  inboundTaskSchemaIds: Set<string>,
  schemaMapperConfigs: Array<SchemaMapperConfig>
): Array<SchemaMapperConfig> | null {
  return recursivelyGetMappers({
    inboundTaskSchemaIds,
    schemaMapperConfigs: schemaMapperConfigs.filter(
      ({toTaskSchemaId}) => toTaskSchemaId === node.taskSchemaId
    ),
    mapperConfigsList: [],
    mappedTaskSchemaIds: new Set(),
  });
}
